import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Pencil, UserPlus, Unlock, RotateCcw, Trash2, Building2, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { name: "", societe: "", tel: "", email: "", dep_equestre: "", dep_pro_sport: "", password: "" };
const splitDeps = (s) => s.split(/[\s,;]+/).map((d) => d.trim()).filter(Boolean);

const DepChips = ({ deps, testId }) => (
  <div className="flex flex-wrap gap-1 max-w-[260px]" data-testid={testId}>
    {deps.length ? deps.map((d) => (
      <span key={d} className="rounded-md bg-[#faf7f1] border border-[#e0d3ba] px-1.5 py-0.5 text-[11px] font-semibold text-[#6b5229]">{d}</span>
    )) : <span className="text-xs text-muted-foreground">—</span>}
  </div>
);

function RevendeurDialog({ open, onClose, initial, authHeader, onSaved }) {
  const [f, setF] = useState(EMPTY);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    setErr("");
    setF(initial ? { ...EMPTY, ...initial, dep_equestre: initial.dep_equestre.join(", "), dep_pro_sport: initial.dep_pro_sport.join(", ") } : EMPTY);
  }, [initial, open]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const save = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    const body = { name: f.name, societe: f.societe, tel: f.tel, email: f.email,
      dep_equestre: splitDeps(f.dep_equestre), dep_pro_sport: splitDeps(f.dep_pro_sport), password: f.password || null };
    try {
      if (initial) await axios.put(`${API}/admin/revendeurs/${initial.id}`, body, { headers: authHeader() });
      else await axios.post(`${API}/admin/revendeurs`, body, { headers: authHeader() });
      toast.success(initial ? "Revendeur mis à jour." : "Revendeur créé.");
      onSaved();
      onClose();
    } catch (x) {
      setErr(x?.response?.data?.detail || "Enregistrement impossible.");
    } finally { setBusy(false); }
  };

  const field = (k, label, props = {}) => (
    <div>
      <Label className="text-sm font-semibold">{label}</Label>
      <Input value={f[k]} onChange={set(k)} data-testid={`revendeur-${k}-input`} className="mt-1.5 h-11" {...props} />
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="revendeur-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">{initial ? `Modifier ${initial.name}` : "Ajouter un revendeur"}</DialogTitle>
          <DialogDescription>Les nouveaux leads sont attribués selon ces départements dès l'enregistrement.</DialogDescription>
        </DialogHeader>
        <form onSubmit={save} className="grid sm:grid-cols-2 gap-4">
          {field("name", "Nom (NOM Prénom)", { required: true })}
          {field("societe", "Société")}
          {field("tel", "Téléphone")}
          {field("email", "E-mail (identifiant de connexion)", { type: "email", required: true })}
          <div className="sm:col-span-2">{field("dep_equestre", "Départements Équestre (vans)", { placeholder: "ex. 24, 33, 47" })}</div>
          <div className="sm:col-span-2">{field("dep_pro_sport", "Départements Pro & Sport (remorques fourgon)", { placeholder: "ex. 24, 33, 2A" })}</div>
          {!initial && (
            <div className="sm:col-span-2">
              {field("password", "Mot de passe initial (facultatif)", { placeholder: "Vide = mot de passe provisoire commun", type: "password", minLength: 8, autoComplete: "new-password" })}
              <p className="text-xs text-muted-foreground mt-1">Le revendeur devra le changer à sa première connexion.</p>
            </div>
          )}
          {err && (
            <p className="sm:col-span-2 flex gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2" data-testid="revendeur-error">
              <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" /> {err}
            </p>
          )}
          <DialogFooter className="sm:col-span-2">
            <Button type="button" variant="outline" onClick={onClose} className="rounded-full">Annuler</Button>
            <Button type="submit" disabled={busy} data-testid="revendeur-save" className="rounded-full bg-[#9E7B41] hover:bg-[#876634]">
              {busy ? "Enregistrement…" : "Enregistrer"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function DefaultContactCard({ contact, authHeader, onSaved }) {
  const [f, setF] = useState(contact);
  useEffect(() => setF(contact), [contact]);
  if (!f) return null;
  const save = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/admin/default-contact`, f, { headers: authHeader() });
      toast.success("Contact par défaut mis à jour.");
      onSaved();
    } catch (x) { toast.error(x?.response?.data?.detail?.[0]?.msg || x?.response?.data?.detail || "Enregistrement impossible."); }
  };
  const inp = (k, label, type = "text") => (
    <div><Label className="text-xs font-semibold">{label}</Label>
      <Input type={type} value={f[k] || ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`default-${k}-input`}
        className="mt-1 h-10" required={k === "vendeur" || k === "email"} /></div>
  );
  return (
    <form onSubmit={save} data-testid="default-contact-form" className="bg-white rounded-2xl border border-border p-5">
      <h3 className="font-heading font-extrabold text-[#2b2b2b] flex items-center gap-2"><Building2 className="h-5 w-5 text-[#9E7B41]" /> Contact par défaut</h3>
      <p className="text-xs text-muted-foreground mt-1">Reçoit les leads des départements sans revendeur.</p>
      <div className="mt-3 grid sm:grid-cols-4 gap-3 items-end">
        {inp("vendeur", "Nom")}{inp("societe", "Société")}{inp("tel", "Téléphone")}{inp("email", "E-mail", "email")}
      </div>
      <Button type="submit" size="sm" data-testid="default-contact-save" className="mt-3 rounded-full bg-[#2b2b2b] hover:bg-[#434343]">Enregistrer</Button>
    </form>
  );
}

export function RevendeursPanel({ authHeader, onReset, onUnlock, onDelete, onChanged }) {
  const [data, setData] = useState({ revendeurs: [], default_contact: null });
  const [edit, setEdit] = useState(undefined);
  const load = useCallback(async () => {
    try {
      const { data: d } = await axios.get(`${API}/admin/revendeurs`, { headers: authHeader() });
      setData(d);
    } catch { toast.error("Chargement des revendeurs impossible."); }
  }, [authHeader]);
  useEffect(() => { load(); }, [load]);
  const refresh = () => { load(); onChanged(); };
  const act = (fn) => async (v) => { await fn(v); load(); };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-2xl border border-border overflow-x-auto">
        <div className="flex items-center justify-between gap-3 p-4 border-b border-border">
          <h2 className="font-heading text-lg font-extrabold text-[#2b2b2b]">Revendeurs ({data.revendeurs.length})</h2>
          <Button onClick={() => setEdit(null)} data-testid="revendeur-add" className="rounded-full bg-[#9E7B41] hover:bg-[#876634]">
            <UserPlus className="h-4 w-4 mr-1.5" /> Ajouter un revendeur
          </Button>
        </div>
        <table className="w-full text-sm" data-testid="admin-revendeurs-table">
          <thead className="bg-[#faf7f1] text-left text-xs uppercase tracking-wider text-muted-foreground">
            <tr><th className="px-4 py-3">Revendeur</th><th className="px-4 py-3">Contact</th>
              <th className="px-4 py-3">Équestre</th><th className="px-4 py-3">Pro & Sport</th><th className="px-4 py-3"></th></tr>
          </thead>
          <tbody>
            {data.revendeurs.map((v) => (
              <tr key={v.id} className="border-t border-border align-top" data-testid={`revendeur-row-${v.id}`}>
                <td className="px-4 py-3">
                  <div className="font-semibold text-[#2b2b2b]">{v.name}
                    {v.locked && <span data-testid={`vendeur-locked-${v.id}`} className="ml-2 inline-block rounded-full bg-red-50 text-red-700 border border-red-200 px-2 py-0.5 text-[11px] font-semibold align-middle">Bloqué</span>}
                  </div>
                  <div className="text-xs text-muted-foreground">{v.societe}</div>
                </td>
                <td className="px-4 py-3 text-xs"><div>{v.email}</div><div className="text-muted-foreground">{v.tel}</div></td>
                <td className="px-4 py-3"><DepChips deps={v.dep_equestre} testId={`revendeur-eq-${v.id}`} /></td>
                <td className="px-4 py-3"><DepChips deps={v.dep_pro_sport} testId={`revendeur-ps-${v.id}`} /></td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <Button size="sm" onClick={() => setEdit(v)} data-testid={`revendeur-edit-${v.id}`} className="rounded-full mr-2 bg-[#2b2b2b] hover:bg-[#434343]">
                    <Pencil className="h-4 w-4 mr-1.5" /> Modifier
                  </Button>
                  {v.locked && (
                    <Button size="sm" variant="outline" onClick={() => act(onUnlock)(v)} data-testid={`vendeur-unlock-${v.id}`} className="rounded-full mr-2">
                      <Unlock className="h-4 w-4 mr-1.5" /> Débloquer
                    </Button>
                  )}
                  <Button size="sm" variant="outline" onClick={() => act(onReset)(v)} data-testid={`vendeur-reset-${v.id}`} className="rounded-full mr-2" title="Réinitialiser le mot de passe">
                    <RotateCcw className="h-4 w-4" />
                  </Button>
                  <button onClick={() => act(onDelete)(v.id)} data-testid={`vendeur-delete-${v.id}`} className="text-red-500 hover:text-red-700 align-middle" title="Supprimer">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </td>
              </tr>
            ))}
            {data.revendeurs.length === 0 && (
              <tr><td colSpan={5} className="px-4 py-10 text-center text-muted-foreground">Aucun revendeur.</td></tr>
            )}
          </tbody>
        </table>
      </div>
      <DefaultContactCard contact={data.default_contact} authHeader={authHeader} onSaved={load} />
      <RevendeurDialog open={edit !== undefined} initial={edit || null} onClose={() => setEdit(undefined)} authHeader={authHeader} onSaved={refresh} />
    </div>
  );
}
